<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { CheckIcon } from '@heroicons/vue/20/solid';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
});

const { isClassic } = useDesignSet();

const isBrandDirect = checkBrandDirect(props.domain);
const brandCta = props.domain?.settings?.brand_cta || 'Jetzt beraten lassen';

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const serviceSingular = props.service === 'wp' ? 'Wärmepumpe' : 'Solaranlage';
const verbLohnen = props.service === 'wp' ? 'lohnen' : 'lohnt';

const benefits = props.service === 'wp' ? [
    { title: 'Heizkosten senken', desc: 'Sparen Sie bis zu 70 % Ihrer monatlichen Heizkosten durch den Umstieg auf modernste Wärmepumpen-Technologie.' },
    { title: 'Unabhängigkeit gewinnen', desc: 'Machen Sie sich unabhängiger von fossilen Energieträgern und dauerhaft steigenden Wärmekosten.' },
    { title: 'Wertsteigerung der Immobilie', desc: 'Eine moderne Wärmepumpe steigert die Energieeffizienzklasse und den Marktwert Ihres Hauses nachhaltig.' },
    { title: 'Staatliche Förderung nutzen', desc: 'Profitieren Sie von bis zu 70 % KfW-Förderung für Wärmepumpen sowie ergänzenden regionalen Programmen.' }
] : [
    { title: 'Stromkosten senken', desc: 'Erzeugen Sie Ihren eigenen Strom und sparen Sie bis zu 80 % Ihrer jährlichen Stromkosten mit einer Solaranlage.' },
    { title: 'Unabhängigkeit gewinnen', desc: 'Machen Sie sich unabhängig von steigenden Strompreisen und erzeugen Sie Ihren eigenen sauberen Strom.' },
    { title: 'Wertsteigerung der Immobilie', desc: 'Eine Photovoltaikanlage steigert den Marktwert Ihres Hauses nachhaltig – ein Pluspunkt bei jedem Verkauf.' },
    { title: 'Staatliche Vorteile nutzen', desc: 'Profitieren Sie von 0 % MwSt. auf Solaranlagen, attraktiver Einspeisevergütung und regionalen Förderprogrammen.' }
];

const comparisonRows = props.service === 'wp' ? [
    { label: 'Heizkosten', with: 'Deutlich geringer', without: 'Steigend' },
    { label: 'Investition', with: 'Durch F\u00f6rderung reduziert', without: 'Volle Energiekosten bleiben' },
    { label: 'Ersparnis (20 Jahre)', with: 'Hohe laufende Entlastung', without: 'Keine Entlastung' },
    { label: 'CO\u2082-Fu\u00dfabdruck', with: 'Deutlich reduziert', without: 'Hoch' },
    { label: 'Immobilienwert', with: 'Deutlich gesteigert', without: 'Unver\u00e4ndert' },
] : [
    { label: 'Stromkosten', with: 'Deutlich geringer', without: 'Steigend' },
    { label: 'Investition', with: '0 % MwSt. seit 2023', without: 'Volle Stromkosten bleiben' },
    { label: 'Ersparnis (20 Jahre)', with: '20.000\u201340.000 \u20ac', without: 'Keine Ersparnis' },
    { label: 'CO\u2082-Fu\u00dfabdruck', with: '4\u20135 t/Jahr weniger', without: 'Hoch' },
    { label: 'Immobilienwert', with: 'Deutlich gesteigert', without: 'Unver\u00e4ndert' },
];
</script>

<template>
    <Head :title="`Vorteile - Warum ${serviceLabel} sich gerade jetzt ${verbLohnen}`">
        <meta name="description" :content="`Entdecken Sie die Vorteile: niedrigere Energiekosten, staatliche Förderungen und eine nachhaltige Wertsteigerung Ihrer Immobilie mit ${serviceLabel}.`" />
        <meta property="og:title" :content="`Vorteile - Warum ${serviceLabel} sich gerade jetzt ${verbLohnen}`" />
        <meta property="og:type" content="website" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="benefits" :heroMode="!isClassic" :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
        <!-- ===== HERO ===== -->
        <section
            class="ds-hero"
            :class="{ 'ds-hero--with-image': !isClassic }"
        >
            <div v-if="!isClassic" class="ds-hero__overlay"></div>
            <div class="ds-hero__inner">
                <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span v-if="!isClassic" class="ds-hero__badge">Vorteile</span>
                    <h1 class="ds-hero__title">
                        Warum {{ serviceLabel }} <span class="ds-hero__accent">sich gerade jetzt</span> {{ verbLohnen }}.
                    </h1>
                    <p class="ds-hero__subtitle">Die Kombination aus hohen Energiekosten, attraktiven Förderungen und moderner Technik macht {{ serviceLabel }} aktuell besonders wirtschaftlich.</p>
                </div>
            </div>
        </section>

        <!-- ===== BENEFITS GRID ===== -->
        <section class="ds-section ds-section--green">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 class="ds-section__heading">Ihre Vorteile auf einen Blick</h2>
                <div class="ds-benefits-grid">
                    <div
                        v-for="b in benefits"
                        :key="b.title"
                        class="ds-benefit-card"
                    >
                        <div class="ds-benefit-card__inner">
                            <div class="ds-benefit-card__icon-wrap">
                                <CheckIcon class="ds-benefit-card__icon" />
                            </div>
                            <div class="ds-benefit-card__body">
                                <h3 class="ds-benefit-card__title">{{ b.title }}</h3>
                                <p class="ds-benefit-card__desc">{{ b.desc }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== COMPARISON ===== -->
        <section class="ds-section ds-section--alt">
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 class="ds-comparison__heading">Der direkte Vergleich.</h2>
                <p class="ds-comparison__subheading">Sehen Sie selbst, wie sich eine {{ serviceSingular }} langfristig auszahlt.</p>


                <!-- Desktop: table -->
                <div class="ds-comparison-table-wrap">
                    <table class="ds-comparison-table">
                        <thead>
                            <tr>
                                <th class="ds-comparison-table__th"></th>
                                <th class="ds-comparison-table__th ds-comparison-table__th--accent">Mit {{ serviceSingular }}</th>
                                <th class="ds-comparison-table__th ds-comparison-table__th--muted">Ohne {{ serviceSingular }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in comparisonRows" :key="row.label" class="ds-comparison-table__row">
                                <td class="ds-comparison-table__td ds-comparison-table__td--label">{{ row.label }}</td>
                                <td class="ds-comparison-table__td ds-comparison-table__td--with">
                                    <span class="ds-comparison-table__check" aria-hidden="true">&#10003;</span>
                                    {{ row.with }}
                                </td>
                                <td class="ds-comparison-table__td ds-comparison-table__td--without">{{ row.without }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Mobile: card layout -->
                <div class="ds-comparison-cards">
                    <div v-for="row in comparisonRows" :key="row.label" class="ds-comparison-card">
                        <div class="ds-comparison-card__label">{{ row.label }}</div>
                        <div class="ds-comparison-card__values">
                            <div class="ds-comparison-card__col">
                                <div class="ds-comparison-card__col-label ds-comparison-card__col-label--accent">Mit {{ serviceSingular }}</div>
                                <div class="ds-comparison-card__col-value">{{ row.with }}</div>
                            </div>
                            <div class="ds-comparison-card__col ds-comparison-card__col--right">
                                <div class="ds-comparison-card__col-label ds-comparison-card__col-label--muted">Ohne</div>
                                <div class="ds-comparison-card__col-value ds-comparison-card__col-value--muted">{{ row.without }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== CTA ===== -->
        <section class="ds-cta" :class="{ 'ds-cta--immersive': !isClassic }">
            <div class="ds-cta__inner max-w-5xl mx-auto px-6 lg:px-10 text-center">
                <h2 class="ds-cta__title">{{ isBrandDirect ? 'Überzeugt? ' + brandCta + '.' : 'Überzeugt? Jetzt vergleichen.' }}</h2>
                <p class="ds-cta__subtitle">{{ isBrandDirect ? 'Fordern Sie jetzt Ihr kostenloses Angebot an und sparen Sie bares Geld.' : 'Starten Sie jetzt Ihren kostenlosen Vergleich und sparen Sie bares Geld.' }}</p>
                <div class="max-w-2xl mx-auto mt-10">
                    <div class="p-6 md:p-10" :style="{ background: 'var(--dv-card, #FFFFFF)', border: '1px solid var(--dv-border, #E8E4DB)', borderRadius: '6px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }">
                        <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                    </div>
                </div>
            </div>
        </section>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* ===================================================================
   SECTION FADE-IN ANIMATION
   =================================================================== */
.section-fade-in {
    opacity: 0;
    transform: translateY(20px);
    animation: sectionReveal 0.5s ease-out forwards;
}

@keyframes sectionReveal {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===================================================================
   HERO - driven by design-set CSS custom properties
   =================================================================== */
.ds-hero {
    position: relative;
    min-height: var(--ds-hero-min-height, 280px);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dv-section, #f9f9f9);
    overflow: hidden;
    transition: min-height 0.3s ease;
}

.ds-hero--with-image {
    background: linear-gradient(135deg, var(--dv-dark, #1a1a2e) 0%, color-mix(in srgb, var(--dv-dark, #1a1a2e) 80%, var(--brand-accent)) 100%);
}

.ds-hero__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-hero-overlay-style, linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%));
    z-index: 1;
}

.ds-hero__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 20% 50%, var(--dv-glow-a, rgba(137,111,218,0.25)) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 30%, var(--dv-glow-b, rgba(251,144,125,0.2)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.25);
    z-index: 2;
    mix-blend-mode: screen;
}

.ds-hero__inner {
    position: relative;
    z-index: 3;
    padding: 100px 0 60px;
    width: 100%;
}

.ds-hero__badge {
    display: inline-block;
    padding: 6px 20px;
    border-radius: 24px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 24px;
}

.ds-hero__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-hero-title-size, clamp(28px, 4vw, 42px));
    font-weight: var(--ds-hero-title-weight, 700);
    line-height: 1.15;
    margin-bottom: 14px;
    text-shadow: var(--ds-hero-text-shadow, none);
}

.ds-hero--with-image .ds-hero__title { color: #fff; }
.ds-hero:not(.ds-hero--with-image) .ds-hero__title { color: var(--dv-text, #333); }

.ds-hero__accent {
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-hero--with-image .ds-hero__accent {
    color: var(--brand-accent-fg, var(--brand-accent));
    text-shadow: 0 0 30px rgba(251, 144, 125, 0.3);
}

.ds-hero__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 17px;
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto;
}

.ds-hero--with-image .ds-hero__subtitle { color: rgba(255, 255, 255, 0.8); }
.ds-hero:not(.ds-hero--with-image) .ds-hero__subtitle { color: var(--dv-muted, #666); }

/* ===================================================================
   SECTIONS
   =================================================================== */
.ds-section {
    padding: 64px 0;
    background: var(--dv-page, #fff);
    position: relative;
}

@media (min-width: 768px) {
    .ds-section { padding: 80px 0; }
}

.ds-section--alt {
    background: var(--dv-section, #f9f9f9);
}

.ds-section__heading {
    font-family: 'Lexend', sans-serif;
    text-align: center;
    font-size: var(--ds-section-title-size, clamp(24px, 3vw, 32px));
    font-weight: var(--ds-hero-title-weight, 700);
    color: var(--dv-text, #333);
    margin-bottom: 40px;
    line-height: 1.25;
}


@media (min-width: 768px) {
    .ds-section__heading { margin-bottom: 52px; }
}

/* ===================================================================
   BENEFITS GRID - cards with hover elevation
   =================================================================== */
.ds-benefits-grid {
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr;
}

@media (min-width: 640px) {
    .ds-benefits-grid { gap: 28px; }
}

@media (min-width: 768px) {
    .ds-benefits-grid { grid-template-columns: repeat(2, 1fr); gap: 28px; }
}

.ds-benefit-card {
    position: relative;
    border-radius: var(--dv-radius-lg, 12px);
    overflow: hidden;
    background: var(--dv-card, #fff);
    border: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    transform: translateZ(0);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out;
    cursor: default;
}

.ds-benefit-card:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06);
}

.ds-benefit-card:focus-visible {
    outline: 3px solid var(--brand-accent);
    outline-offset: 2px;
}


.ds-benefit-card__inner {
    padding: 28px 28px 28px 24px;
    display: flex;
    align-items: flex-start;
    gap: 18px;
}

.ds-benefit-card__icon-wrap {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: var(--brand-accent-soft, rgba(251,144,125,0.1));
}

.ds-benefit-card__icon {
    width: 20px;
    height: 20px;
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-benefit-card__title {
    font-family: 'Lexend', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--dv-text, #333);
    margin-bottom: 8px;
    line-height: 1.35;
}

.ds-benefit-card__desc {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.65;
}

/* ===================================================================
   COMPARISON TABLE (desktop)
   =================================================================== */
.ds-comparison__heading {
    font-family: 'Lexend', sans-serif;
    text-align: center;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: var(--ds-hero-title-weight, 700);
    color: var(--dv-text, #333);
    margin-bottom: 12px;
    line-height: 1.25;
}


.ds-comparison__subheading {
    font-family: 'Source Sans 3', sans-serif;
    text-align: center;
    font-size: 16px;
    color: var(--dv-muted, #666);
    margin-bottom: 32px;
    line-height: 1.6;
}

.ds-comparison-table-wrap {
    display: none;
    overflow-x: auto;
    border-radius: var(--dv-radius-lg, 12px);
    border: none;
    background: var(--dv-card, #fff);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
}

@media (min-width: 640px) {
    .ds-comparison-table-wrap { display: block; }
}

.ds-comparison-table {
    width: 100%;
    border-collapse: collapse;
}

.ds-comparison-table__th {
    padding: 18px 24px;
    text-align: left;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: var(--dv-muted, #999);
}

.ds-comparison-table__th--accent {
    text-align: center;
    font-family: 'Lexend', sans-serif;
    font-weight: 600;
    color: var(--brand-accent-fg, var(--brand-accent));
    background: var(--brand-accent-soft, rgba(251,144,125,0.04));
}

.ds-comparison-table__th--muted {
    text-align: center;
    color: var(--dv-muted, #999);
}

.ds-comparison-table__row {
    border-top: 1px solid var(--dv-border, #eee);
    transition: background 200ms ease-out;
}

.ds-comparison-table__row:hover {
    background: var(--dv-section, #f9f9f9);
}

.ds-comparison-table__td {
    padding: 18px 24px;
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
}

.ds-comparison-table__td--label {
    color: var(--dv-text, #333);
    font-weight: 600;
}

.ds-comparison-table__td--with {
    text-align: center;
    color: var(--dv-text, #333);
    font-weight: 500;
    background: var(--brand-accent-soft, rgba(251,144,125,0.04));
}

.ds-comparison-table__check {
    display: inline-block;
    font-weight: 700;
    color: var(--brand-accent-fg, var(--brand-accent));
    margin-right: 4px;
}

.ds-comparison-table__td--without {
    text-align: center;
    color: var(--dv-muted, #999);
    font-size: 14px;
}

/* ===================================================================
   COMPARISON CARDS (mobile)
   =================================================================== */
.ds-comparison-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (min-width: 640px) {
    .ds-comparison-cards { display: none; }
}

.ds-comparison-card {
    padding: 18px;
    border: none;
    border-radius: 10px;
    background: var(--dv-card, #fff);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out;
}

.ds-comparison-card__label {
    font-family: 'Lexend', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--dv-text, #333);
    margin-bottom: 10px;
}

.ds-comparison-card__values {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.ds-comparison-card__col--right {
    text-align: right;
}

.ds-comparison-card__col-label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 3px;
}

.ds-comparison-card__col-label--accent {
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-comparison-card__col-label--muted {
    color: var(--dv-muted, #999);
    font-weight: 400;
}

.ds-comparison-card__col-value {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-text, #333);
}

.ds-comparison-card__col-value--muted {
    font-size: 14px;
    color: var(--dv-muted, #999);
}

/* ===================================================================
   CTA SECTION
   =================================================================== */
.ds-cta {
    padding: 80px 0;
    background: var(--dv-page, #fff);
    position: relative;
    overflow: hidden;
}

@media (min-width: 768px) {
    .ds-cta { padding: 100px 0; }
}

.ds-cta--immersive {
    background: var(--dv-dark, #1a1a2e);
}

.ds-cta__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 30% 50%, var(--dv-glow-a, rgba(137,111,218,0.2)) 0%, transparent 60%),
        radial-gradient(ellipse at 70% 50%, var(--dv-glow-b, rgba(251,144,125,0.15)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.3);
    pointer-events: none;
    z-index: 0;
}

.ds-cta__inner {
    position: relative;
    z-index: 1;
}

.ds-cta__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: var(--ds-hero-title-weight, 700);
    margin-bottom: 12px;
    line-height: 1.25;
}

.ds-cta--immersive .ds-cta__title { color: #fff; }
.ds-cta:not(.ds-cta--immersive) .ds-cta__title { color: var(--dv-text, #333); }

.ds-cta__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 16px;
    line-height: 1.7;
    margin-bottom: 32px;
}

.ds-cta--immersive .ds-cta__subtitle { color: rgba(255, 255, 255, 0.7); }
.ds-cta:not(.ds-cta--immersive) .ds-cta__subtitle { color: var(--dv-muted, #666); }

.ds-cta__funnel {
    max-width: 480px;
    margin: 0 auto;
}
</style>
